import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  countries,
  gradesByCountry,
  topicUnits,
  topicCategories,
  getUnitsForLevel,
} from '../data/curriculumData';
import './UnitsPage.css';

const STORAGE_KEY = 'mathbox_units_prefs';

/** Persist lightweight preferences to localStorage. */
function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}
function savePrefs(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

const UnitsPage = () => {
  const { isDarkTheme } = useTheme();
  const { profile } = useAuth();

  // --- Classification mode ---------------------------------------------------
  const [mode, setMode] = useState('school'); // 'school' | 'topic'

  // --- School-level state ----------------------------------------------------
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');

  // --- Topic filter state ----------------------------------------------------
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // --- Settings panel visibility ---------------------------------------------
  const [showSettings, setShowSettings] = useState(false);

  // --- Derived units ---------------------------------------------------------
  const [units, setUnits] = useState([]);

  // Restore preferences from localStorage / profile on mount
  useEffect(() => {
    const prefs = loadPrefs();
    if (prefs.mode) setMode(prefs.mode);
    if (prefs.country) setSelectedCountry(prefs.country);
    if (prefs.grade) setSelectedGrade(prefs.grade);
    if (prefs.category) setActiveCategory(prefs.category);

    // If no saved country, try to derive from profile / onboarding
    if (!prefs.country && profile?.onboarding_data) {
      const lang = profile.language_preference || profile.onboarding_data?.language;
      const langCountryMap = { english: 'US', french: 'FR', italian: 'IT' };
      const guessed = langCountryMap[lang?.toLowerCase()];
      if (guessed) setSelectedCountry(guessed);
    }
  }, [profile]);

  // Persist whenever prefs change
  useEffect(() => {
    savePrefs({
      mode,
      country: selectedCountry,
      grade: selectedGrade,
      category: activeCategory,
    });
  }, [mode, selectedCountry, selectedGrade, activeCategory]);

  // Compute displayed units
  const computeUnits = useCallback(() => {
    if (mode === 'school') {
      if (!selectedCountry || !selectedGrade) return [];
      const grades = gradesByCountry[selectedCountry] || [];
      const gradeObj = grades.find((g) => g.id === selectedGrade);
      if (!gradeObj) return [];
      return getUnitsForLevel(selectedCountry, gradeObj.level);
    }
    // topic mode
    let filtered = topicUnits;
    if (activeCategory !== 'all') {
      filtered = filtered.filter((u) => u.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.title.toLowerCase().includes(q) ||
          u.description.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [mode, selectedCountry, selectedGrade, activeCategory, searchQuery]);

  useEffect(() => {
    setUnits(computeUnits());
  }, [computeUnits]);

  // When country changes, reset grade
  const handleCountryChange = (code) => {
    setSelectedCountry(code);
    setSelectedGrade('');
  };

  const grades = gradesByCountry[selectedCountry] || [];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className={`units-page ${isDarkTheme ? 'dark' : 'light'}`}>
      <div className="units-page-container">
        {/* ---- Header ---- */}
        <div className="units-page-header">
          <div>
            <h1>Units</h1>
            <p className="units-page-subtitle">
              {mode === 'school'
                ? 'Browse units by school level and curriculum'
                : 'Explore mathematics by topic'}
            </p>
          </div>
          <button
            className="units-page-settings-btn"
            onClick={() => setShowSettings((v) => !v)}
            title="Settings"
          >
            ⚙️ Settings
          </button>
        </div>

        {/* ---- Classification Toggle ---- */}
        <div className="units-classification-toggle">
          <button
            className={`units-toggle-btn ${mode === 'school' ? 'active' : ''}`}
            onClick={() => setMode('school')}
          >
            🏫 By School Level
          </button>
          <button
            className={`units-toggle-btn ${mode === 'topic' ? 'active' : ''}`}
            onClick={() => setMode('topic')}
          >
            📚 By Topic
          </button>
        </div>

        {/* ---- Settings Panel (collapsible) ---- */}
        {showSettings && (
          <div className="units-settings-panel card">
            <h3>Settings</h3>

            {/* Country selector */}
            <div className="units-setting-row">
              <label htmlFor="units-country-select">Country</label>
              <select
                id="units-country-select"
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
              >
                <option value="">Select a country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Grade selector (only in school mode) */}
            {mode === 'school' && selectedCountry && (
              <div className="units-setting-row">
                <label htmlFor="units-grade-select">Grade / Year</label>
                <select
                  id="units-grade-select"
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                >
                  <option value="">Select your grade</option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* ---- School-level inline selectors (always visible in school mode) ---- */}
        {mode === 'school' && (
          <div className="units-school-selectors">
            <div className="units-inline-select">
              <label>Country</label>
              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
              >
                <option value="">Choose…</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedCountry && (
              <div className="units-inline-select">
                <label>Grade</label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* ---- Topic filters ---- */}
        {mode === 'topic' && (
          <div className="units-topic-filters">
            <div className="units-category-pills">
              <button
                className={`units-pill ${activeCategory === 'all' ? 'active' : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All
              </button>
              {topicCategories.map((cat) => (
                <button
                  key={cat.id}
                  className={`units-pill ${activeCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="units-search-wrapper">
              <span className="units-search-icon">🔍</span>
              <input
                type="text"
                className="units-search-input"
                placeholder="Search topics…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* ---- Units grid ---- */}
        {units.length === 0 ? (
          <div className="units-page-empty">
            <div className="units-page-empty-icon">
              {mode === 'school' ? '🏫' : '📚'}
            </div>
            <h3>
              {mode === 'school'
                ? 'Select a country and grade to see units'
                : 'No topics match your filter'}
            </h3>
            <p>
              {mode === 'school'
                ? 'Use the selectors above to load the curriculum for your grade.'
                : 'Try a different category or clear your search.'}
            </p>
          </div>
        ) : (
          <div className="units-page-grid">
            {units.map((unit, index) => (
              <div key={unit.id} className="units-page-card card">
                {unit.icon && (
                  <span className="units-page-card-icon">{unit.icon}</span>
                )}
                <div className="units-page-card-number">
                  Unit {index + 1}
                </div>
                <h3 className="units-page-card-title">{unit.title}</h3>
                {unit.description && (
                  <p className="units-page-card-desc">{unit.description}</p>
                )}
                <div className="units-page-card-arrow">→</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnitsPage;
